import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useCookies } from 'next-client-cookies';
import { renderToStaticMarkup } from 'react-dom/server';
import { Log } from 'helpers/errorLogger';
import { useRouter } from 'i18n/routing';
/* eslint-disable @typescript-eslint/ban-ts-comment */
//@ts-ignore
import { markdown as MD } from 'markdown';
import { usePageLoader } from 'providers/loader';

export interface Props {
  markdown: string;
  className?: string;
}

const actions: Record<string, () => void> = {
  onetrust: () => {
    window?.OneTrust?.ToggleInfoDisplay();
  },
};

const EXTERNAL_LINK_ICON_MARKUP = renderToStaticMarkup(
<ArrowTopRightOnSquareIcon className="inline ml-2 pb-4 size-20" aria-hidden="true" />
);

const appendExternalLinkIcon = (anchor: HTMLAnchorElement) => {
  if (anchor.querySelector('[data-external-link-icon="true"]')) return;
  anchor.insertAdjacentHTML('beforeend', EXTERNAL_LINK_ICON_MARKUP);
};

const Markdown: React.FC<Props> = ({ markdown, className = '' }) => {
  const [safeMarkdown, setSafeMarkdown] = useState('');
  const router = useRouter();
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;

  const { showLoader } = usePageLoader();

  useEffect(() => {
    if (typeof markdown !== 'string') {
      Log.error(
        new Error(
          `Markdown: Invalid markdown property. Expected string but received ${typeof markdown}`
        )
      );
      return;
    }

    const sanitized = DOMPurify.sanitize(MD.toHTML(markdown));
    
    const container = document.createElement('div');
    container.innerHTML = sanitized;
    container.querySelectorAll('a[href^="http://"], a[href^="https://"]').forEach((anchor) => {
      if (!anchor.getAttribute('target')) {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      }
    });

    container.querySelectorAll('a[target="_blank"]').forEach((anchor) => {
      appendExternalLinkIcon(anchor as HTMLAnchorElement);
    });

    setSafeMarkdown(container.innerHTML);
  }, [markdown]);

  const handleAnchorNavigation = (anchor: HTMLAnchorElement, e?: React.SyntheticEvent) => {
    const href = anchor.getAttribute('href');

    if (!href) return;

    if (href.startsWith('onetrust')) {
      e?.preventDefault();
      actions.onetrust?.();
      return;
    }

    if (
      href === '#' ||
      href.startsWith('#') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:')
    ) {
      return;
    }

    // Links that already opt into a new tab/window are left to the browser.
    if (anchor.target === '_blank') {
      return;
    }

    if (
      href.startsWith('http://') ||
      href.startsWith('https://')
    ) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
      return;
    }

    if (href.startsWith('/')) {
      e?.preventDefault();

      showLoader();

      const finalPath = locale ? `${href}?hl=${locale}` : href;
      router.push(finalPath);
    }
  };

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a') as HTMLAnchorElement | null;

    if (!anchor) return;

    handleAnchorNavigation(anchor, e);
  };

  return (
    // Sonar: click handler is used for event delegation on child anchor elements
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={className}
      onClick={handleClick}
      onTouchEnd={handleClick}
      onKeyDown={() => {}}
      dangerouslySetInnerHTML={{ __html: safeMarkdown }}
    />
  );
};

export default Markdown;
